import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { PurchaseOrderApproval } from '../models/PurchaseOrder';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class PurchaseOrderApprovalService {
  /** Base do microserviço de compras, sem `?code=` (a chave é enviada em cada request). */
  private readonly baseUrl = this.stripShoppingMicroserviceQuery(
    String(environment.microservices?.shopping ?? '')
  );
  private readonly apiUrl = `${this.baseUrl}/purchase-order-approvals`;
  private readonly staffBaseUrl = environment.microservices.staff;
  
  private approvalsSubject = new BehaviorSubject<PurchaseOrderApproval[]>([]);
  public approvals$ = this.approvalsSubject.asObservable();

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  // ===== HELPERS DE AUTENTICAÇÃO / FUNCTION KEY (consistente com PurchaseOrderService) =====

  /** Remove query/trailing slash de `microservices.shopping` para montar paths HTTP. */
  private stripShoppingMicroserviceQuery(raw: string): string {
    let s = raw.trim();
    const q = s.indexOf('?');
    if (q >= 0) {
      s = s.slice(0, q);
    }
    return s.replace(/\/+$/, '');
  }

  /** `code` embutido em `shopping: 'http://.../api?code=...'` (comum em Functions locais). */
  private getCodeFromShoppingMicroserviceUrl(): string {
    const raw = String(environment.microservices?.shopping ?? '').trim();
    const q = raw.indexOf('?');
    if (q < 0) {
      return '';
    }
    try {
      const qs = raw.slice(q + 1);
      return (new URLSearchParams(qs).get('code') ?? '').trim();
    } catch {
      return '';
    }
  }

  /** Resolve a function key consultando, em ordem, o `code` do microserviço, `functionKey` e fallbacks. */
  private getShoppingFunctionCode(): string {
    const env = environment as Record<string, unknown>;
    const candidates = [
      this.getCodeFromShoppingMicroserviceUrl(),
      environment.functionKey,
      environment.shoppingPurchaseOrdersListCode,
      env['shoppingPurchaseRequestsCode']
    ];
    for (const c of candidates) {
      const v = String(c ?? '').trim();
      if (v) {
        return v;
      }
    }
    return '';
  }

  /** Anexa `code=` quando há function key configurada. */
  private appendShoppingFunctionCode(params: HttpParams): HttpParams {
    const fnCode = this.getShoppingFunctionCode();
    return fnCode ? params.set('code', fnCode) : params;
  }

  /** Monta `{ headers: Bearer, params? }` reusando o JWT atual. */
  private authOpts(params?: HttpParams): { headers: HttpHeaders; params?: HttpParams } {
    const raw = this.authService.getToken();
    const token = typeof raw === 'string' ? raw.trim() : '';
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return params ? { headers, params } : { headers };
  }

  /**
   * Detecta envelope `{ error, httpStatus, type, hint? }` (HTTP 200 com erro embutido)
   * e converte em throwError compatível com `errorService.handleHttpError`.
   * Mesmo padrão usado em `PurchaseOrderService.unwrapShoppingResponse`.
   */
  private unwrapShoppingResponse<T>(resp: T): T {
    if (!resp || typeof resp !== 'object') return resp;
    const r = resp as Record<string, unknown>;
    const errMsg = r['error'];
    if (typeof errMsg !== 'string' || !errMsg.trim()) return resp;
    const httpStatus =
      typeof r['httpStatus'] === 'number'
        ? (r['httpStatus'] as number)
        : typeof r['status'] === 'number'
          ? (r['status'] as number)
          : 409;
    const type = typeof r['type'] === 'string' ? (r['type'] as string) : '';
    const hint = typeof r['hint'] === 'string' ? (r['hint'] as string).trim() : '';
    const combined = hint ? `${errMsg.trim()}\n\n${hint}` : errMsg.trim();
    throw {
      status: httpStatus,
      statusText: type || 'Conflict',
      error: { error: combined, type, httpStatus, hint, originalMessage: errMsg.trim() },
      message: combined,
      name: 'ShoppingSoftError'
    };
  }

  /** Monta `HttpParams` com `code` e (se possível) `currentStaffId` para chamadas do microserviço. */
  private buildShoppingActionParams(): HttpParams {
    let params = new HttpParams();
    params = this.appendShoppingFunctionCode(params);
    const sid = this.authService.getStaffIdFromToken();
    if (sid != null && Number.isFinite(Number(sid)) && Number(sid) > 0) {
      params = params.set('currentStaffId', String(sid));
    }
    return params;
  }

  // ===== OPERAÇÕES BÁSICAS =====

  /**
   * Obtém todas as aprovações
   */
  getAllApprovals(): Observable<PurchaseOrderApproval[]> {
    return this.http.get<PurchaseOrderApproval[]>(this.apiUrl).pipe(
      tap(approvals => this.approvalsSubject.next(approvals)),
      map(approvals => approvals.map(approval => this.mapDates(approval)))
    );
  }

  /**
   * Obtém uma aprovação por ID
   */
  getApprovalById(id: number): Observable<PurchaseOrderApproval> {
    return this.http.get<PurchaseOrderApproval>(`${this.apiUrl}/${id}`).pipe(
      map(approval => this.mapDates(approval))
    );
  }

  /**
   * Cria uma nova aprovação
   */
  createApproval(approval: Partial<PurchaseOrderApproval>): Observable<PurchaseOrderApproval> {
    return this.http.post<PurchaseOrderApproval>(this.apiUrl, approval).pipe(
      map(approval => this.mapDates(approval)),
      tap(() => this.refreshApprovals())
    );
  }

  /**
   * Atualiza uma aprovação
   */
  updateApproval(id: number, approval: Partial<PurchaseOrderApproval>): Observable<any> {
    return this.http.put(`${this.apiUrl}/${id}`, approval).pipe(
      tap(() => this.refreshApprovals())
    );
  }

  /**
   * Exclui uma aprovação
   */
  deleteApproval(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`).pipe(
      tap(() => this.refreshApprovals())
    );
  }

  // ===== CONSULTAS POR PEDIDO =====

  /**
   * Obtém aprovações por pedido de compra
   */
  getApprovalsByPurchaseOrder(purchaseOrderId: number): Observable<PurchaseOrderApproval[]> {
    return this.http.get<PurchaseOrderApproval[]>(`${this.baseUrl}/purchase-orders/${purchaseOrderId}/approvals`).pipe(
      map(approvals => approvals.map(approval => this.mapDates(approval)))
    );
  }

  /**
   * Obtém próxima aprovação pendente
   */
  getNextPendingApproval(purchaseOrderId: number): Observable<PurchaseOrderApproval> {
    return this.http.get<PurchaseOrderApproval>(`${this.baseUrl}/purchase-orders/${purchaseOrderId}/next-approval`).pipe(
      map(approval => this.mapDates(approval))
    );
  }

  /**
   * Obtém última aprovação
   */
  getLastApproval(purchaseOrderId: number): Observable<PurchaseOrderApproval> {
    return this.http.get<PurchaseOrderApproval>(`${this.baseUrl}/purchase-orders/${purchaseOrderId}/last-approval`).pipe(
      map(approval => this.mapDates(approval))
    );
  }

  // ===== CONSULTAS POR FUNCIONÁRIO =====

  /**
   * Obtém aprovações por funcionário
   */
  getApprovalsByStaff(staffId: number): Observable<PurchaseOrderApproval[]> {
    return this.http.get<PurchaseOrderApproval[]>(`${this.staffBaseUrl}/staff/${staffId}/approvals`).pipe(
      map(approvals => approvals.map(approval => this.mapDates(approval)))
    );
  }

  /**
   * Obtém aprovações pendentes por funcionário
   */
  getPendingApprovalsByStaff(staffId: number): Observable<PurchaseOrderApproval[]> {
    return this.http.get<PurchaseOrderApproval[]>(`${this.staffBaseUrl}/staff/${staffId}/pending-approvals`).pipe(
      map(approvals => approvals.map(approval => this.mapDates(approval)))
    );
  }

  /**
   * Obtém histórico de aprovações por funcionário
   */
  getApprovalHistoryByStaff(staffId: number): Observable<PurchaseOrderApproval[]> {
    return this.http.get<PurchaseOrderApproval[]>(`${this.staffBaseUrl}/staff/${staffId}/approval-history`).pipe(
      map(approvals => approvals.map(approval => this.mapDates(approval)))
    );
  }

  // ===== CONSULTAS AVANÇADAS =====

  /**
   * Obtém aprovações filtradas
   */
  getApprovalsFiltered(filters: any): Observable<PurchaseOrderApproval[]> {
    let params = new HttpParams();
    
    Object.keys(filters).forEach(key => {
      if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
        params = params.set(key, filters[key].toString());
      }
    });

    return this.http.get<PurchaseOrderApproval[]>(`${this.apiUrl}/filtered`, { params }).pipe(
      map(approvals => approvals.map(approval => this.mapDates(approval)))
    );
  }

  /**
   * Obtém aprovações por ação
   */
  getApprovalsByAction(action: string): Observable<PurchaseOrderApproval[]> {
    return this.http.get<PurchaseOrderApproval[]>(`${this.apiUrl}/action/${action}`).pipe(
      map(approvals => approvals.map(approval => this.mapDates(approval)))
    );
  }

  /**
   * Obtém aprovações por nível
   */
  getApprovalsByLevel(approvalLevel: string): Observable<PurchaseOrderApproval[]> {
    return this.http.get<PurchaseOrderApproval[]>(`${this.apiUrl}/level/${approvalLevel}`).pipe(
      map(approvals => approvals.map(approval => this.mapDates(approval)))
    );
  }

  // ===== OPERAÇÕES DE APROVAÇÃO =====
  //
  // Regra de negócio (faixa de aprovação): só existem DUAS ações — aprovar e rejeitar.
  // Não há "solicitar alterações" neste fluxo (essa ação é exclusiva do gestor
  // designado da solicitação simples, via `purchase-requests/{id}/manager-decision`).

  /**
   * Aprova uma etapa de aprovação por faixa.
   * `POST {shoppingBaseUrl}/purchase-order-approvals/{approvalId}/approve?code=&currentStaffId=`
   * Body: `{ comments }` — usa Bearer do JWT atual e trata envelope soft-error do microserviço.
   */
  approveApproval(id: number, comments?: string): Observable<unknown> {
    const params = this.buildShoppingActionParams();
    const body = { comments: (comments ?? '').trim() };
    return this.http
      .post(`${this.apiUrl}/${id}/approve`, body, this.authOpts(params))
      .pipe(
        map((resp) => this.unwrapShoppingResponse(resp)),
        tap(() => this.refreshApprovals())
      );
  }

  /**
   * Rejeita uma etapa de aprovação por faixa.
   * `POST {shoppingBaseUrl}/purchase-order-approvals/{approvalId}/reject?code=&currentStaffId=`
   * Body: `{ rejectionReason }`.
   */
  rejectApproval(id: number, rejectionReason: string): Observable<unknown> {
    const params = this.buildShoppingActionParams();
    const body = { rejectionReason: (rejectionReason ?? '').trim() };
    return this.http
      .post(`${this.apiUrl}/${id}/reject`, body, this.authOpts(params))
      .pipe(
        map((resp) => this.unwrapShoppingResponse(resp)),
        tap(() => this.refreshApprovals())
      );
  }

  // ===== VALIDAÇÕES DE APROVAÇÃO =====

  /**
   * Verifica se funcionário pode aprovar
   */
  canStaffApprove(purchaseOrderId: number, staffId: number): Observable<{ canApprove: boolean }> {
    return this.http.get<{ canApprove: boolean }>(`${this.baseUrl}/purchase-orders/${purchaseOrderId}/can-approve/${staffId}`);
  }

  /**
   * Verifica se funcionário já aprovou
   */
  hasStaffAlreadyApproved(purchaseOrderId: number, staffId: number): Observable<{ hasApproved: boolean }> {
    return this.http.get<{ hasApproved: boolean }>(`${this.baseUrl}/purchase-orders/${purchaseOrderId}/has-approved/${staffId}`);
  }

  // ===== DELEGAÇÃO DE APROVAÇÃO =====

  /**
   * Delega uma aprovação
   */
  delegateApproval(id: number, delegatedToStaffId: number, delegationExpiry: Date): Observable<any> {
    const request = { delegatedToStaffId, delegationExpiry: delegationExpiry.toISOString() };
    return this.http.post(`${this.apiUrl}/${id}/delegate`, request).pipe(
      tap(() => this.refreshApprovals())
    );
  }

  /**
   * Obtém aprovações delegadas
   */
  getDelegatedApprovals(staffId: number): Observable<PurchaseOrderApproval[]> {
    return this.http.get<PurchaseOrderApproval[]>(`${this.staffBaseUrl}/staff/${staffId}/delegated-approvals`).pipe(
      map(approvals => approvals.map(approval => this.mapDates(approval)))
    );
  }

  /**
   * Obtém delegações expiradas
   */
  getExpiredDelegations(): Observable<PurchaseOrderApproval[]> {
    return this.http.get<PurchaseOrderApproval[]>(`${this.apiUrl}/expired-delegations`).pipe(
      map(approvals => approvals.map(approval => this.mapDates(approval)))
    );
  }

  // ===== CONSULTAS POR NÍVEL =====

  /**
   * Obtém aprovações pendentes por nível
   */
  getPendingApprovalsByLevel(approvalLevel: string): Observable<PurchaseOrderApproval[]> {
    return this.http.get<PurchaseOrderApproval[]>(`${this.apiUrl}/pending/level/${approvalLevel}`).pipe(
      map(approvals => approvals.map(approval => this.mapDates(approval)))
    );
  }

  /**
   * Obtém aprovações por funcionário e nível
   */
  getApprovalsByStaffAndLevel(staffId: number, approvalLevel: string): Observable<PurchaseOrderApproval[]> {
    return this.http.get<PurchaseOrderApproval[]>(`${this.staffBaseUrl}/staff/${staffId}/approvals/level/${approvalLevel}`).pipe(
      map(approvals => approvals.map(approval => this.mapDates(approval)))
    );
  }

  // ===== RELATÓRIOS E ESTATÍSTICAS =====

  /**
   * Obtém estatísticas de aprovações
   */
  getApprovalsStatistics(): Observable<any> {
    return this.http.get(`${this.apiUrl}/statistics`);
  }

  /**
   * Obtém aprovações em atraso
   */
  getOverdueApprovals(): Observable<PurchaseOrderApproval[]> {
    return this.http.get<PurchaseOrderApproval[]>(`${this.apiUrl}/overdue`).pipe(
      map(approvals => approvals.map(approval => this.mapDates(approval)))
    );
  }

  /**
   * Obtém aprovações que vencem em X dias
   */
  getApprovalsDueInDays(days: number): Observable<PurchaseOrderApproval[]> {
    return this.http.get<PurchaseOrderApproval[]>(`${this.apiUrl}/due-in/${days}`).pipe(
      map(approvals => approvals.map(approval => this.mapDates(approval)))
    );
  }

  // ===== CONFIGURAÇÃO DE FLUXO =====

  /**
   * Configura fluxo de aprovação
   */
  setupApprovalFlow(purchaseOrderId: number, totalAmount: number): Observable<any> {
    const request = { totalAmount };
    return this.http.post(`${this.baseUrl}/purchase-orders/${purchaseOrderId}/setup-approval-flow`, request);
  }

  /**
   * Obtém níveis de aprovação necessários
   */
  getRequiredApprovalLevels(totalAmount: number): Observable<{ requiredLevels: string[] }> {
    return this.http.get<{ requiredLevels: string[] }>(`${this.apiUrl}/required-levels/${totalAmount}`);
  }

  // ===== OPERAÇÕES EM LOTE =====

  /**
   * Aprovação em lote
   */
  bulkApprove(approvalIds: number[], staffId: number): Observable<any> {
    const request = { approvalIds, staffId };
    return this.http.post(`${this.apiUrl}/bulk/approve`, request).pipe(
      tap(() => this.refreshApprovals())
    );
  }

  /**
   * Rejeição em lote
   */
  bulkReject(approvalIds: number[], staffId: number, rejectionReason: string): Observable<any> {
    const request = { approvalIds, staffId, rejectionReason };
    return this.http.post(`${this.apiUrl}/bulk/reject`, request).pipe(
      tap(() => this.refreshApprovals())
    );
  }

  // ===== DASHBOARD DE APROVAÇÃO =====

  /**
   * Obtém dashboard de aprovação
   */
  getApprovalDashboard(staffId: number): Observable<any> {
    return this.http.get(`${this.staffBaseUrl}/staff/${staffId}/approval-dashboard`);
  }

  /**
   * Obtém métricas de aprovação
   */
  getApprovalMetrics(from?: Date, to?: Date): Observable<any> {
    let params = new HttpParams();
    if (from) params = params.set('from', from.toISOString());
    if (to) params = params.set('to', to.toISOString());

    return this.http.get(`${this.apiUrl}/metrics`, { params });
  }

  /**
   * Obtém performance de aprovação por funcionário
   */
  getStaffApprovalPerformance(staffId: number, from?: Date, to?: Date): Observable<any> {
    let params = new HttpParams();
    if (from) params = params.set('from', from.toISOString());
    if (to) params = params.set('to', to.toISOString());

    return this.http.get(`${this.staffBaseUrl}/staff/${staffId}/approval-performance`, { params });
  }

  // ===== MÉTODOS PRIVADOS =====

  /**
   * Converte strings de data em objetos Date
   */
  private mapDates(approval: any): PurchaseOrderApproval {
    if (approval.approvalDate) approval.approvalDate = new Date(approval.approvalDate);
    if (approval.delegationExpiry) approval.delegationExpiry = new Date(approval.delegationExpiry);
    if (approval.createdAt) approval.createdAt = new Date(approval.createdAt);
    if (approval.updatedAt) approval.updatedAt = new Date(approval.updatedAt);
    
    return approval as PurchaseOrderApproval;
  }

  /**
   * Atualiza a lista de aprovações
   */
  private refreshApprovals(): void {
    this.getAllApprovals().subscribe();
  }

  // ===== MÉTODOS DE FORMATAÇÃO =====

  /**
   * Formata a ação para exibição
   */
  getActionDisplayName(action: string): string {
    const actionMap: { [key: string]: string } = {
      'Pendente': 'Aguardando Aprovação',
      'Aprovado': 'Aprovado',
      'Rejeitado': 'Rejeitado',
      'Solicitado Alteração': 'Solicitou Alterações'
    };
    return actionMap[action] || action;
  }

  /**
   * Obtém a cor da ação
   */
  getActionColor(action: string): string {
    const colorMap: { [key: string]: string } = {
      'Pendente': 'warning',
      'Aprovado': 'success',
      'Rejeitado': 'danger',
      'Solicitado Alteração': 'info'
    };
    return colorMap[action] || 'secondary';
  }

  /**
   * Obtém o ícone da ação
   */
  getActionIcon(action: string): string {
    const iconMap: { [key: string]: string } = {
      'Pendente': 'clock',
      'Aprovado': 'check-circle',
      'Rejeitado': 'x-circle',
      'Solicitado Alteração': 'edit'
    };
    return iconMap[action] || 'circle';
  }

  /**
   * Formata o nível de aprovação para exibição
   */
  getApprovalLevelDisplayName(approvalLevel: string): string {
    const levelMap: { [key: string]: string } = {
      'Supervisor': 'Supervisor',
      'Gerente': 'Gerente',
      'Diretor': 'Diretor',
      'Presidente': 'Presidente',
      'Automático': 'Aprovação Automática'
    };
    return levelMap[approvalLevel] || approvalLevel;
  }

  /**
   * Obtém a cor do nível de aprovação
   */
  getApprovalLevelColor(approvalLevel: string): string {
    const colorMap: { [key: string]: string } = {
      'Supervisor': 'info',
      'Gerente': 'primary',
      'Diretor': 'warning',
      'Presidente': 'danger',
      'Automático': 'success'
    };
    return colorMap[approvalLevel] || 'secondary';
  }
}
